import React, { useState, useEffect } from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import { X } from "lucide-react";

// --------- MAP ICONS ----------
const iconRed = new L.Icon({
  iconUrl:
    "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const iconYellow = new L.Icon({
  iconUrl:
    "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-yellow.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const iconDriverActive = new L.Icon({
  iconUrl: "http://bumppy.in/packky/api/v1/uploads/images/driver_icon.png",
  iconSize: [30, 30],
  iconAnchor: [15, 30],
  popupAnchor: [0, -25],
});

// -------- STATUS OPTIONS ----------
const statusOptions = [
  "unassigned",
  "assigned",
  "acknowledged",
  "arrived",
  "food is ready",
  "picked up",
  "completed",
  "successful",
  "cancelled",
];

export default function TaskDetailsModal({
  show,
  task,
  onClose,
  isLoading,
  assignmentLogs = [],
  statusChangeLogs = [],
  showLogs,
  setShowLogs,
  onStatusChange,
  onFetchLogs,
}) {
  const getLatestStatus = () => {
    if (statusChangeLogs?.length > 0) {
      return (
        statusChangeLogs[statusChangeLogs.length - 1]?.status ||
        task?.status ||
        ""
      ).toLowerCase();
    }
    return (task?.status || "").toLowerCase();
  };

  const [selectedStatus, setSelectedStatus] = useState(getLatestStatus());

  useEffect(() => {
    setSelectedStatus(getLatestStatus());
  }, [task, statusChangeLogs]);

  if (!show || !task) return null;

  const hasMapCoords =
    (task?.dropoff_latitude || task?.pickup_latitude) &&
    (task?.dropoff_longitude || task?.pickup_longitude);

  //  LOGS TOGGLE
  const handleToggleLogs = async () => {
    if (!showLogs && onFetchLogs) {
      await onFetchLogs(task.pk_task_id);
    }
    setShowLogs(!showLogs);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full p-4 relative text-sm max-h-[90vh] overflow-y-auto">
        {/* -------- HEADER -------- */}
        <div className="flex justify-between items-center mb-4 pb-3 border-b sticky top-0 bg-white z-10">
          <h2 className="text-lg font-semibold">Delivery Task Details</h2>

          <div className="flex items-center gap-2">
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="border rounded px-3 py-1 text-xs bg-white"
            >
              {statusOptions.map((st) => (
                <option key={st} value={st}>
                  {st.toUpperCase()}
                </option>
              ))}
            </select>

            <button
              onClick={async () => {
                const success = await onStatusChange?.(
                  task?.pk_task_id,
                  selectedStatus,
                );
              }}
              className="bg-blue-500 text-white px-3 py-1 rounded text-xs"
            >
              Change Status
            </button>

            <button
              title="Close"
              className="flex items-center justify-center text-gray-800 w-8 h-8 rounded-full shadow hover:bg-gray-400"
              onClick={onClose}
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* -------- MAP + INFO TABLE -------- */}
        <div className="flex flex-col lg:flex-row gap-4">
          {hasMapCoords && (
            <div className="w-full lg:w-1/2 h-64 rounded overflow-hidden border">
              <MapContainer
                center={[
                  task?.dropoff_latitude || task?.pickup_latitude,
                  task?.dropoff_longitude || task?.pickup_longitude,
                ]}
                zoom={14}
                scrollWheelZoom={false}
                style={{ height: "100%", width: "100%" }}
              >
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

                {task?.pickup_latitude && task?.pickup_longitude && (
                  <Marker
                    position={[task.pickup_latitude, task.pickup_longitude]}
                    icon={iconRed}
                  >
                    <Popup>Pickup Location</Popup>
                  </Marker>
                )}

                {task?.dropoff_latitude && task?.dropoff_longitude && (
                  <Marker
                    position={[task.dropoff_latitude, task.dropoff_longitude]}
                    icon={iconYellow}
                  >
                    <Popup>Delivery Location</Popup>
                  </Marker>
                )}

                {task?.driver_latitude && task?.driver_longitude && (
                  <Marker
                    position={[task.driver_latitude, task.driver_longitude]}
                    icon={iconDriverActive}
                  >
                    <Popup>Driver Location</Popup>
                  </Marker>
                )}
              </MapContainer>
            </div>
          )}

          {/* INFO TABLE */}
          <div className="w-full lg:w-1/2 overflow-x-auto">
            <table className="w-full text-xs border">
              <tbody>
                <Row label="Customer" value={task?.customer_name} />
                <Row label="Contact" value={task?.contact_number} />
                <Row label="Email" value={task?.email_address} />
                <Row label="Driver" value={task?.driver_name} />
                <Row label="Driver Mobile" value={task?.driver_mobile} />
                <Row label="Distance" value={task?.taskDistance} />
                <Row label="ETA" value={task?.taskEstimatedTime} last />
              </tbody>
            </table>
          </div>
        </div>

        {/* ADDRESSES */}
        <div className="mt-4 text-xs">
          <div className="mb-2">
            <strong>Pickup Address:</strong> {task?.pickup_address || "N/A"}
          </div>
          <div>
            <strong>Delivery Address:</strong> {task?.delivery_address || "N/A"}
          </div>
        </div>

        {/* LOGS ACCORDION  */}
        <div className="mt-4">
          <div
            onClick={handleToggleLogs}
            className="px-4 py-2 flex justify-between items-center cursor-pointer bg-gray-100 hover:bg-gray-200"
          >
            <h3 className="text-sm font-semibold">Status & Assignment Logs</h3>
            <span
              className={`transition-transform ${showLogs ? "rotate-180" : ""}`}
            >
              ▼
            </span>
          </div>

          <div
            className={`overflow-hidden transition-all duration-300 ${
              showLogs ? "max-h-[1000px]" : "max-h-0"
            }`}
          >
            <div className="p-3 border-t space-y-6 text-sm">
              {statusChangeLogs.length === 0 && assignmentLogs.length === 0 && (
                <div className="text-gray-500 text-center py-4">
                  No logs available
                </div>
              )}

              {statusChangeLogs.length > 0 && (
                <div className="border rounded p-3">
                  <h3 className="text-xs font-semibold mb-3">
                    Status Change Logs
                  </h3>

                  <div className="flex justify-center gap-6 flex-wrap">
                    {statusChangeLogs.map((log, idx) => {
                      const status = log.status?.toLowerCase();

                      const statusColorMap = {
                        unassigned: "bg-gray-400",
                        acknowledged: "bg-blue-500",
                        arrived: "bg-green-500",
                        successful: "bg-green-700",
                        completed: "bg-green-700",
                        "food is ready": "bg-purple-500",
                        "picked up": "bg-orange-500",
                        assigned: "bg-yellow-500",
                        cancelled: "bg-red-500",
                      };

                      const bgColor = statusColorMap[status] || "bg-blue-500";

                      return (
                        <div key={idx} className="flex flex-col items-center">
                          <div
                            className={`w-9 h-9 flex items-center justify-center rounded-full text-white text-sm font-bold ${bgColor}`}
                          >
                            {idx + 1}
                          </div>

                          <div className="text-[10px] mt-1 text-center font-medium">
                            {log.status?.toUpperCase() || "N/A"}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {assignmentLogs.length > 0 && (
                <div className="border rounded p-3">
                  <h3 className="text-xs font-semibold mb-2">
                    Assignment Logs
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="table-auto w-full text-xs border">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="p-2 border">Assign Type</th>
                          <th className="p-2 border">Driver ID</th>
                          <th className="p-2 border">Driver Name</th>
                          <th className="p-2 border">Task Status</th>
                          <th className="p-2 border">Status</th>
                          <th className="p-2 border">Assignment Date</th>
                          <th className="p-2 border">Process Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {assignmentLogs.map((log, idx) => (
                          <tr key={idx}>
                            <td className="p-2 border">
                              {log.assignType || "N/A"}
                            </td>
                            <td className="p-2 border">
                              {log.driverId || "N/A"}
                            </td>
                            <td className="p-2 border">
                              {log.driverName?.trim() || "N/A"}
                            </td>
                            <td className="p-2 border">
                              {log.taskStatus || "N/A"}
                            </td>
                            <td className="p-2 border">
                              {log.status || "N/A"}
                            </td>
                            <td className="p-2 border">
                              {log.assignmentDate || "N/A"}
                            </td>
                            <td className="p-2 border">
                              {log.processDate || "N/A"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* DELIVERY ITEMS */}
        {task?.delivery_details?.items?.length > 0 ? (
          <>
            <h3 className="text-sm font-semibold mt-4 mb-2">Delivery Items</h3>
            <div className="overflow-x-auto">
              <table className="min-w-[700px] w-full text-xs border">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-2 py-2 text-left">Item</th>
                    <th className="px-2 py-2 text-left">Type</th>
                    <th className="px-2 py-2 text-left">Description</th>
                    <th className="px-2 py-2 text-left">W×H×L</th>
                    <th className="px-2 py-2 text-left">Weight</th>
                  </tr>
                </thead>
                <tbody>
                  {task.delivery_details.items.map((item, index) => (
                    <tr key={index} className="border-t">
                      <td className="px-2 py-2">{item?.itemName || "N/A"}</td>
                      <td className="px-2 py-2">{item?.itemType || "N/A"}</td>
                      <td className="px-2 py-2">
                        {item?.itemDescription || "N/A"}
                      </td>
                      <td className="px-2 py-2">
                        {item?.width || 0}×{item?.height || 0}×
                        {item?.length || 0}
                      </td>
                      <td className="px-2 py-2">{item?.weight || "N/A"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <p className="text-xs text-center text-gray-500 mt-4">
            No delivery item details available
          </p>
        )}
      </div>
    </div>
  );
}

// Helper row component
const Row = ({ label, value, last }) => (
  <tr className={last ? "" : "border-b"}>
    <td className="font-semibold px-2 py-1 bg-gray-100">{label}</td>
    <td className="px-2 py-1">{value || "N/A"}</td>
  </tr>
);
